import {
  ApiError,
  Field,
  Hypertext,
  HypertextSize,
  InfoPanel,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalTitle,
  RadioBox,
  TruncableText,
  useAppContext,
  useCloseModal,
  useModalConfirmation,
  useSetApiErrors,
  useToastManager
} from "@quortex/front-common"
import { useMatchesParams } from "@quortex/front-common/adapters/react-location"
import { useSearch } from "@tanstack/react-location"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { isEmpty } from "lodash-es"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"
import urlJoin from "url-join"
import type { OriginServerFormType } from "@/pages/ContentSteering/Settings/form"
import { usePlayPoolList } from "@/queries/playPools"
import { useSegmentList } from "@/queries/segments"
import { useUpdateWorkspace } from "@/queries/workspaces"

interface OriginServerFormProps {
  originServer?: api.switchns.Workspace["originServer"]
}

const OriginServerForm: FC<OriginServerFormProps> = ({ originServer }) => {
  const closeModal = useCloseModal()
  const toastManager = useToastManager()

  const form = useForm<OriginServerFormType>({
    defaultValues: originServer ? { originServer } : {}
  })

  const { openConfirmationModal } = useModalConfirmation()

  const { mutateAsync: updateWorkspace } = useMutation(useUpdateWorkspace())

  const { data: segmentList } = useSuspenseQuery({ ...useSegmentList() })
  const { data: playPoolsWithContentSteering } = useSuspenseQuery({
    ...usePlayPoolList({ contentSteeringEnabled: "true" })
  })

  const firstPlayPoolsWithContentSteering = playPoolsWithContentSteering?.[0]

  const { orgUuid } = useMatchesParams()

  const setApiErrors = useSetApiErrors(form)
  const { apps } = useAppContext()

  const search = useSearch()

  // Function to update origin server
  const updateOriginServer = async (originServer: api.switchns.Workspace["originServer"]) => {
    await updateWorkspace({ originServer })
    closeModal()
  }

  // Handle form submission
  const onSubmit = async (originServer: api.switchns.Workspace["originServer"]) => {
    try {
      if (originServer === "switch") {
        if (isEmpty(segmentList)) {
          await updateOriginServer(originServer)
        } else {
          openConfirmationModal({
            message:
              "Disconnecting will stop syncing CDNs from Play. Your current configuration will remain in Switch.",
            okBtnLabel: "Disconnect",
            onAccept: () => {
              updateOriginServer(originServer)
            },
            onDeny: () => {
              return
            },
            title: "Disconnect Switch and Play?",
            variant: "destructive"
          })
        }
      }
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        setApiErrors(e, { originServer })
      } else {
        toastManager.error({ apiError: e })
      }
    }
  }

  const playlink = {
    children: "Go to Play",
    external: true,

    href: urlJoin(
      apps?.play || "",
      "/organizations/",
      orgUuid || "",
      `/play/settings/content-steering`
    ),
    size: HypertextSize.SMALL
  } as const

  return (
    <>
      <Modal
        data-testid="origin-server-switch-modal"
        show={search.modal === "origin_server_switch_info"}
      >
        <ModalContent data-testid="add-segment-modal-content">
          <ModalHeader onClose={closeModal}>
            <ModalTitle>CDNs</ModalTitle>
          </ModalHeader>

          <ModalBody>
            <div key="play-pool-info" className="flex flex-col gap-sm">
              <div>
                Switch will use the CDN list provided by{" "}
                <strong>{firstPlayPoolsWithContentSteering?.name}</strong> Play pool.
              </div>
              <InfoPanel
                className="w-full"
                color="info"
                data-testid="included-cdns-panel"
                title="Included CDNs"
              >
                <ul>
                  {firstPlayPoolsWithContentSteering?.cdns.map(cdn => (
                    <li
                      key={`${firstPlayPoolsWithContentSteering.uuid}-${cdn.uuid}`}
                      data-testid={`${firstPlayPoolsWithContentSteering.uuid}-${cdn.uuid}`}
                    >
                      <TruncableText>{cdn.name}</TruncableText>
                    </li>
                  ))}
                </ul>
              </InfoPanel>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>

      <form className="flex w-full flex-col" noValidate>
        <FormProvider {...form}>
          <Field label="Origin server" name="originServer">
            {fieldName => (
              <div className="flex w-full gap-sm">
                <RadioBox
                  data-testid="third-party-btn"
                  {...form.register(fieldName)}
                  checked={originServer === "switch"}
                  className="basis-1/2"
                  value="switch"
                  onChange={() => onSubmit("switch")}
                >
                  <div className="flex flex-col gap-xs">
                    <div className="font-medium">Third-party</div>
                    <div className="typo-sm-regular">
                      Use Quortex Switch with any streaming solution
                    </div>
                    <Hypertext
                      data-testid="learn-more-content-steering-link"
                      external
                      href="https://help.quortex.io/en/article/packagers-and-content-steering-1ec0ftn/"
                      size="small"
                    >
                      Learn more
                    </Hypertext>
                  </div>
                </RadioBox>
                <RadioBox
                  data-testid="quortex-play-btn"
                  {...form.register(fieldName)}
                  checked={originServer === "play"}
                  className="basis-1/2"
                  disabled={originServer === "switch" || isEmpty(playPoolsWithContentSteering)}
                  value="play"
                  onChange={() => onSubmit("play")}
                >
                  <div className="flex items-center justify-between gap-xs">
                    <div className="flex flex-col gap-xs">
                      <div className="font-medium">Quortex Play</div>
                      {originServer === "switch" && (
                        <div className="typo-sm-regular">Connect Switch and Play seamlessly</div>
                      )}
                    </div>
                  </div>
                  {originServer === "play" && !isEmpty(playPoolsWithContentSteering) && (
                    <InfoPanel
                      className="col-start-2 w-full"
                      color="warning"
                      data-testid="connected-play-pool-panel"
                      inline
                      links={[
                        {
                          children: "View CDNs",
                          to: { search: { modal: "origin_server_switch_info" } }
                        }
                      ]}
                      withIcon={false}
                    >
                      <span className="typo-sm-bold">
                        {firstPlayPoolsWithContentSteering?.name}
                      </span>
                    </InfoPanel>
                  )}
                  {originServer === "switch" && (
                    <div className="relative col-start-2 w-full">
                      <div className="absolute z-50 flex h-full w-full cursor-default items-end">
                        <Hypertext {...playlink} data-testid="play-connection-link" />
                      </div>
                      <Hypertext
                        {...playlink}
                        className="invisible"
                        data-testid="play-connection-link-placeholder"
                      />
                    </div>
                  )}
                </RadioBox>
              </div>
            )}
          </Field>
          <Field
            className="!mt-lg"
            data-testid="quortex-switch-urls-field"
            label="Quortex Switch URLs"
            labelClassName="mt-0 !mb-sm"
            name="Quortex Switch URLs"
            popoverProps={{
              body: (
                <p>
                  In your streaming solution, use those URLs as &quot;Content steering server
                  URL&quot; for HLS playlists & DASH manifests.
                </p>
              ),
              placement: "top"
            }}
          />
        </FormProvider>
      </form>
    </>
  )
}

export default OriginServerForm
